import type { AssetType } from 'src/data/asset';
import type { GenerationTool } from 'src/data/generationTool';
import type { UserSettings } from 'src/data/user';
import type { ID } from 'src/lib/collection';

import {
  EXTERNAL_MESSAGE,
  INCOMING_MESSAGE,
  type IncomingMessageType,
  type InfinityUserFlow,
  OUTGOING_MESSAGE,
} from './const';
import type {
  ExternalMessage,
  ExtractedAsset,
  InfinityResultError,
  InfinitySettings,
  MessageContent,
  OutgoingMessage,
} from './types';

/**
 * Check if the message type is supported as "incoming" message from Extension
 * @param type - message type
 * @returns - true if the message type is supported
 */
export const isSupportedIncomingMessage = (
  type: IncomingMessageType
): type is IncomingMessageType => Object.values(INCOMING_MESSAGE).includes(type);

export const messagePlatformReady = (): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.platformReady,
});

export const messageFrameViewReady = (): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.frameViewReady,
});

export const messagePlatformInfo = (content: MessageContent): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.platformInfo,
  content,
});

export const messageSubmitInstructions = (content: MessageContent): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.submitInstructions,
  content,
});

export const messageDisplayFullscreen = (fullscreen: boolean): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.displayFullscreen,
  content: {
    fullscreen,
  },
});

export const messageCloseFrame = (): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.closeFrame,
});

export const messageScoreTextResult = ({
  userFlow,
  text,
  score,
  generationTool,
  assetType,
  error = undefined,
}: {
  userFlow: InfinityUserFlow;
  text: string;
  score?: number;
  generationTool?: GenerationTool;
  assetType?: AssetType;
  error?: string;
}): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.scoreTextResult,
  content: {
    userFlow,
    text,
    score,
    generationTool,
    assetType,
    error,
  },
});

export const messageDisplaySuggestionInsights = ({
  generationTool,
  text,
}: {
  generationTool: GenerationTool;
  text: string;
}): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.displaySuggestionInsights,
  content: {
    generationTool,
    text,
  },
});

export const messageApplySuggestion = ({
  assetType,
  text,
  keepFrame,
}: {
  assetType: AssetType;
  text: string;
  keepFrame: boolean;
}): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.applySuggestion,
  content: {
    assetType,
    text,
    keepFrame,
  },
});

export const messageClassifyTextResult = ({
  assetType,
  generationTool,
}: {
  assetType: AssetType;
  generationTool: GenerationTool;
}): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.classifyTextResult,
  content: {
    assetType,
    generationTool,
  },
});

export const messageManualToolSelected = ({
  assetType,
  generationTool,
}: {
  assetType: AssetType;
  generationTool: GenerationTool;
}): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.manualToolSelected,
  content: {
    assetType,
    generationTool,
  },
});

export const messageDictionaryBriefs = (content: MessageContent): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.dictionaryBriefs,
  content,
});

export const messageDictionaryResources = (content: MessageContent): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.dictionaryResources,
  content,
});

export const messageDictionaryWorkspaces = (content: MessageContent): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.dictionaryWorkspaces,
  content,
});

export const messageImportedBrief = (content: MessageContent): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.importedBrief,
  content,
});

export const messageSaveSettings = (settings: InfinitySettings): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.saveSettings,
  content: {
    ...settings,
  },
});

export const externalMessageLogin = (token: string): ExternalMessage => ({
  type: EXTERNAL_MESSAGE.login,
  content: { token },
});

export const externalMessageLogout = (): ExternalMessage => ({
  type: EXTERNAL_MESSAGE.logout,
});

export const externalMessageSettingsChanged = (settings: UserSettings): ExternalMessage => ({
  type: EXTERNAL_MESSAGE.settingsChanged,
  content: { ...settings },
});

export const messageDetectPageAssetsResult = ({
  assets,
  error = undefined,
}: {
  assets?: ExtractedAsset[];
  error?: InfinityResultError;
}): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.detectPageAssetsResult,
  content: {
    assets,
    error,
  },
});

export const messageCreateProjectFromAssetsResult = ({
  projectId,
  error = undefined,
}: {
  projectId?: ID;
  error?: InfinityResultError;
}): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.createProjectFromAssetsResult,
  content: {
    projectId,
    error,
  },
});

export const messageCreateVariationFromAssetResult = ({
  projectId,
  error = undefined,
}: {
  projectId?: ID;
  error?: InfinityResultError;
}): OutgoingMessage => ({
  type: OUTGOING_MESSAGE.createVariationFromAssetResult,
  content: {
    projectId,
    error,
  },
});
